import { useState, useEffect, useCallback } from 'react';
import { ServiceCRUDChatTS } from 'services/ServiceCRUDCHATTS';
import * as yup from 'yup';

const monitoringService = new ServiceCRUDChatTS('/monitoring');

const projectedVolumeSchema = yup.object().shape({
  projectedVolume: yup.number().required(),
  realizedVolume: yup.number().required()
});

const matrixHourDTOSchema = yup.object().shape({
  hourRange: yup.string().nullable(),
  openChats: yup.number().required(),
  closedChats: yup.number().required(),
  closedByTimeout: yup.number().required(),
  tme: yup.string().required(),
  tma: yup.string().required(),
  tta: yup.string().required(),
  tmfr: yup.string().required(),
  tmr: yup.string().required(),
  tmo: yup.string().required(),
  tmInAssigned: yup.string().required(),
  serviceLevel: yup.string().required(),
  attendanceLevel: yup.string().required(),
  chatsAttended: yup.number().required(),
  chatsNotAttended: yup.number().required(),
  chatsCount: yup.number().required(),
  projectedVolume: projectedVolumeSchema.required()
});

const subReasonSchema = yup.object().shape({
  name: yup.string().required(),
  count: yup.number().required()
});

const reasonRankingSchema = yup.object().shape({
  name: yup.string().required(),
  count: yup.number().required(),
  subReasons: yup.array().of(subReasonSchema).required()
});

const serviceLevelSchema = yup.object().shape({
  numberOfValid: yup.number().required(),
  numberOfInvalid: yup.number().required(),
  percentValid: yup.string().required(),
  percentInvalid: yup.string().required()
});

const startChatsCountSchema = yup.object().shape({
  BOT: yup.number().required(),
  HUMAN: yup.number().required()
});

const monitoringResponseSchema = yup.object().shape({
  TMA: yup.string().required(),
  TMO: yup.string().required(),
  TMB: yup.string().required(),
  TME: yup.string().required(),
  TDRD: yup.string().required(),
  TDR: yup.string().required(),
  tmInAssigned: yup.string().required(),
  CHATS_ANSWERED: yup.number().required(),
  CHATS_NOT_ANSWERED: yup.number().required(),
  CHATS_IN_WAITING: yup.number().required(),
  MOST_TIME_IN_WAIT: yup.string().required(),
  AGENTS_LOGGED_IN_QUEUES: yup.array().of(yup.string()).required(),
  SERVICE_LEVEL: serviceLevelSchema.required(),
  START_CHATS_COUNT: startChatsCountSchema.required(),
  MATRIX_MAP_HOUR: yup.array().of(matrixHourDTOSchema).required(),
  REASON_RANKING: yup.array().of(reasonRankingSchema).required()
});

/**
 * Estado inicial alinhado com o schema para evitar undefined na renderização
 */
const initialState = {
  TMA: '00:00:00',
  TME: '00:00:00',
  TMO: '00:00:00',
  TMB: '00:00:00',
  TDRD: '00:00:00',
  TDR: '00:00:00',
  tmInAssigned: '00:00:00',
  CHATS_ANSWERED: 0,
  CHATS_NOT_ANSWERED: 0,
  CHATS_IN_WAITING: 0,
  MOST_TIME_IN_WAIT: '00:00:00',
  AGENTS_LOGGED_IN_QUEUES: [],
  SERVICE_LEVEL: {
    numberOfValid: 0,
    numberOfInvalid: 0,
    percentValid: '0%',
    percentInvalid: '0%'
  },
  START_CHATS_COUNT: {
    BOT: 0,
    HUMAN: 0
  },
  MATRIX_MAP_HOUR: [],
  REASON_RANKING: []
};

/**
 * Hook para monitoramento de filas de chat
 * Busca dados de monitoramento para as filas especificadas
 * @param {Object} [props={}]
 * @param {string[]} [props.queueNames=[]] - Nomes das filas a serem monitoradas
 * @param {boolean} [props.autoRefresh=false] - Se verdadeiro, atualiza os dados automaticamente
 * @param {number} [props.refreshInterval=30000] - Intervalo de atualização em milissegundos
 * @returns {Object} Estado de monitoramento e funções
 */
const useMonitoringChatQueues = (props = {}) => {
  const {
    queueNames = [],
    channels = [],
    agents = [],
    autoRefresh = false,
    refreshInterval = 30000
  } = props;

  const [state, setState] = useState({
    /** @type {yup.InferType<typeof monitoringResponseSchema>} */
    response: initialState,
    isLoading: false,
    isError: false,
    error: null
  });

  const fetch = useCallback(async () => {
    if (queueNames.length === 0 && channels.length === 0) return;

    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      isError: false,
      error: null
    }));

    try {
      const response = await monitoringService.getMonitoringChatQueues(
        queueNames,
        channels,
        agents
      );

      const rawData = response?.data?.data || {};

      const validation = monitoringResponseSchema.safeValidateSync(rawData, {
        abortEarly: false,
        stripUnknown: true
      });

      if (!validation.success) {
        throw {
          name: 'ValidationError',
          message: validation.error.message,
          errors: validation.error.errors
        };
      }

      setState((prevState) => ({
        ...prevState,
        response: validation.data,
        isLoading: false,
        isError: false,
        error: null
      }));
    } catch (err) {
      console.error(err);
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        isError: true,
        error: err
      }));
    }
  }, [
    JSON.stringify(queueNames),
    JSON.stringify(channels),
    JSON.stringify(agents)
  ]);

  useEffect(() => {
    fetch();

    let intervalId = null;
    if (autoRefresh && queueNames.length > 0) {
      intervalId = setInterval(fetch, refreshInterval);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [fetch, autoRefresh, refreshInterval]);

  return {
    ...state,
    fetch,
    queueNames
  };
};

export { useMonitoringChatQueues };
